import type { HttpCore } from '../http';

/**
 * Types in this module mirror `frontend/web-ide/src/services/portal.ts`
 * exactly (same field names, same shapes) — that file is the extraction
 * source, not a redesign.
 */

export type CountryCode = 'NZ' | 'AU' | 'PK' | 'IN' | 'SA' | 'GB' | 'CA' | 'US' | 'OTHER';

export interface EnrolledVertical {
  slug: string;
  name: string;
  founding_member_number: number | null;
  status: 'active' | 'paused' | 'graduated' | 'cancelled';
  enrolled_at: string;
}

export interface CohortMembership {
  cohort_id: string;
  vertical_slug: string;
  started_at: string;
}

export interface UserContext {
  user_id: string;
  email: string;
  role: string;
  onboarding_complete: boolean;
  country_code: CountryCode;
  content_source_slug: string;
  currency: string;
  timezone: string;
  default_language: string;
  rtl_flag: boolean;
  legal_regime: string | null;
  enrolled_verticals: EnrolledVertical[];
  cohort_memberships: CohortMembership[];
  preferred_locale: string;
  requested_content_language?: ContentLanguage;
  resolved_content_language?: ContentLanguage;
  available_content_languages?: ContentLanguage[];
  content_language_fallback?: boolean;
  content_rtl?: boolean;
}

export interface CourseTesterCourse {
  id: string;
  slug: string;
  title: string;
  expiresAt: string;
  href: string;
}

export interface CourseTesterAccess {
  environment: string;
  courses: CourseTesterCourse[];
  hasCourseTesterGrant: boolean;
}

export interface CourseTutorContext {
  courseId: string;
  courseSlug: string;
  courseItemId: string;
  labId: string | null;
  autonomyLevel: 'off' | 'socratic';
}

export interface EnrollmentSummary {
  vertical_slug: string;
  vertical_name: string;
  status: string;
  founding_member_number: number | null;
  enrolled_at: string;
  current_cert_slug: string | null;
  current_cert_name: string | null;
  overall_progress_pct: number;
  next_item_title: string | null;
  next_item_href: string | null;
  next_item_id: string | null;
  next_item_type: 'lecture' | 'reading' | 'lab' | 'assessment' | null;
}

export type TrackRole = 'core' | 'elective';

export interface CertCompletion {
  kind: string;
  required: number;
  completed: number;
}

export interface CertLadderEntry {
  slug: string;
  name: string;
  order: number;
  is_current: boolean;
  is_locked: boolean;
  progress_pct: number;
  track_role: TrackRole;
  is_unlocked: boolean;
  is_complete: boolean;
  completion: CertCompletion | null;
}

export interface TrackSummary {
  core_total: number;
  core_completed: number;
  electives_required: number;
  electives_completed: number;
  is_track_complete: boolean;
}

export interface VerticalDetail {
  slug: string;
  name: string;
  status: string;
  founding_member_number: number | null;
  cert_ladder: CertLadderEntry[];
  track_summary: TrackSummary;
}

export interface CohortMember {
  display_name: string;
  city: string | null;
  country_code: string | null;
  emphasis: string | null;
  is_self: boolean;
}

export interface CohortSummary {
  cohort_id: string | null;
  vertical_slug: string;
  vertical_name: string;
  member_count: number;
  members: CohortMember[];
}

export type ViewMode = 'guided' | 'standard' | 'studio';
export type ShellPreference = 'guided' | 'pro' | 'standard' | 'studio';

export interface UserPreferencesPayload {
  region: string;
  preferredLocale: string;
  scaffoldingPreference: 'auto' | 'high' | 'medium' | 'low';
  viewMode: ViewMode;
  firstStudioAt: string | null;
  shellPreference: ShellPreference;
}

export type ContentLanguage = 'en' | 'ur' | 'pa' | 'ps';

export interface RegionInfo {
  countryCode: string;
  name: string;
  currency: string;
  taxRate: number | null;
  taxLabel: string | null;
  rtlFlag: boolean;
  defaultLanguage: string;
  timezone: string;
  contentSourceSlug: string;
  legalRegime: string | null;
}

export interface GeoPreviewGrant {
  allowed_regions: string[];
  allowed_languages: string[];
}

export interface WaitlistConfirmRequest {
  email: string;
  detected_country: string;
  region?: string | null;
  source?: string;
}

/** GET /users/me/limits — session-manager `user_limits` row. No current web
 *  consumer to lift an exact shape from; fields below are the documented
 *  CLAUDE.md contract, kept open with an index signature for safety. */
export interface UserLimits {
  dailyApiCalls?: number;
  monthlyApiCalls?: number;
  concurrentEnvironments?: number;
  [key: string]: unknown;
}

/** GET /users/me/usage — mirrors `APIUsage.tsx`'s `UsageData`. */
export interface UserUsage {
  daily: { used: number; limit: number; resetAt: string };
  monthly: { used: number; limit: number; resetAt: string };
  breakdown: { openai: number; anthropic: number; other: number };
}

/** GET /users/me/credentials — mirrors `CredentialsPage.tsx`'s `Credential`. */
export interface UserCredential {
  credentialNumber: string;
  certificateTitle: string;
  issuedAt: string;
  revoked: boolean;
  verificationUrl: string;
  addToLinkedInUrl: string;
}

export function createMeModule(core: HttpCore) {
  return {
    getContext: () => core.request<UserContext>('/users/me/context'),
    getCourseTesterAccess: () => core.request<CourseTesterAccess>('/users/me/course-tester-access'),
    getCourseTutorContext: (courseId: string, courseItemId: string) =>
      core.request<CourseTutorContext>('/users/me/course-tutor-context', {
        params: { courseId, courseItemId },
      }),
    getVerticalsDetail: () =>
      core
        .request<{ verticals: VerticalDetail[] }>('/users/me/context/verticals-detail')
        .then((r) => r.verticals),
    getEnrollments: () =>
      core.request<{ enrollments: EnrollmentSummary[] }>('/users/me/enrollments').then((r) => r.enrollments),
    getCohort: () => core.request<{ cohorts: CohortSummary[] }>('/users/me/cohort').then((r) => r.cohorts),
    getPreferences: () =>
      core
        .request<{ preferences: UserPreferencesPayload }>('/users/me/preferences')
        .then((r) => r.preferences),
    updatePreferences: (
      patch: Partial<
        Pick<UserPreferencesPayload, 'viewMode' | 'scaffoldingPreference' | 'shellPreference' | 'preferredLocale'>
      >,
    ) =>
      core
        .request<{ preferences: UserPreferencesPayload }>('/users/me/preferences', {
          method: 'PATCH',
          body: patch,
        })
        .then((r) => r.preferences),
    setContentLanguage: (preferredLocale: ContentLanguage) =>
      core
        .request<{ preferences: UserPreferencesPayload }>('/users/me/preferences', {
          method: 'PATCH',
          body: { preferredLocale },
        })
        .then((r) => r.preferences),
    updateRegion: (countryCode: CountryCode) =>
      core.request<{ message: string; region: RegionInfo }>('/users/me/region', {
        method: 'PATCH',
        body: { countryCode },
      }),
    getLimits: () => core.request<UserLimits>('/users/me/limits'),
    getUsage: () => core.request<UserUsage>('/users/me/usage'),
    getCredentials: () =>
      core.request<{ credentials: UserCredential[] }>('/users/me/credentials').then((r) => r.credentials ?? []),
    completeOnboarding: () => core.request<UserContext>('/users/me/onboarding/complete', { method: 'POST' }),
    getGeoPreviewGrant: () =>
      core
        .request<GeoPreviewGrant | null>('/users/me/geo-preview-grant')
        .then((r) => r ?? null)
        .catch(() => null),
    confirmWaitlist: (body: WaitlistConfirmRequest) =>
      core.request<{ ok: boolean; status: string }>('/waitlist', {
        method: 'POST',
        body: { source: 'blocked_login', ...body },
      }),
  };
}

export type MeModule = ReturnType<typeof createMeModule>;
